import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import PasswordInput from "../components/PasswordInput";
import { Shield, Crown, Lock, Unlock, Plus, Users, FileText, Truck, Search, Trash2, AlertTriangle } from "lucide-react";
import { PLANO_LABELS } from "../lib/planos";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; }
};

export default function GestaoAssinantesPage() {
  const [assinantes, setAssinantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", senha: "", nome_loja: "", telefone: "", plano: "start" });
  const [creating, setCreating] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);
  const [blockMotivo, setBlockMotivo] = useState("");
  const [blockSaving, setBlockSaving] = useState(false);
  // Excluir
  const [delOpen, setDelOpen] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [delSenha, setDelSenha] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [delLoading, setDelLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/super-admin/assinantes`);
      setAssinantes(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar assinantes");
    } finally {
      setLoading(false);
    }
  };

  const criar = async (e) => {
    e?.preventDefault?.();
    setCreating(true);
    try {
      await axios.post(`${API}/super-admin/assinantes`, form);
      toast.success("Assinante criado com sucesso!");
      setCreateOpen(false);
      setForm({ nome: "", email: "", senha: "", nome_loja: "", telefone: "", plano: "start" });
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar assinante");
    } finally {
      setCreating(false);
    }
  };

  const abrirBlock = (a) => {
    setBlockTarget(a);
    setBlockMotivo("");
    setBlockOpen(true);
  };

  const abrirExcluir = (a) => {
    setDelTarget(a);
    setDelSenha("");
    setDelConfirm("");
    setDelOpen(true);
  };

  const confirmarExcluir = async () => {
    if (!delTarget) return;
    if (delConfirm.trim().toUpperCase() !== "EXCLUIR") {
      toast.error('Digite "EXCLUIR" para confirmar');
      return;
    }
    if (!delSenha) {
      toast.error("Informe sua senha de Super Admin");
      return;
    }
    setDelLoading(true);
    try {
      await axios.delete(`${API}/super-admin/assinantes/${delTarget.id}`, {
        data: { senha: delSenha },
      });
      toast.success(`Assinante "${delTarget.nome}" excluído com sucesso!`);
      setDelOpen(false);
      setDelTarget(null);
      setDelSenha("");
      setDelConfirm("");
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao excluir");
    } finally {
      setDelLoading(false);
    }
  };

  const confirmarBlock = async () => {
    if (!blockTarget) return;
    setBlockSaving(true);
    try {
      await axios.put(`${API}/super-admin/assinantes/${blockTarget.id}/status`, {
        bloqueado: !blockTarget.bloqueado,
        motivo: blockMotivo,
      });
      toast.success(blockTarget.bloqueado ? "Assinante desbloqueado!" : "Assinante bloqueado!");
      setBlockOpen(false);
      setBlockTarget(null);
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar status");
    } finally {
      setBlockSaving(false);
    }
  };

  const alterarPlano = async (assinante, novoPlano) => {
    if (!novoPlano || novoPlano === assinante.plano) return;
    // Detecta downgrade que pode exceder limites (ex: usuário com 5 carros virando Start=1 carro)
    const limites = { start: 1, pro: 5, premium: 10, corporativo: null };
    const limiteNovo = limites[novoPlano];
    const carrosAtuais = assinante.stats?.carros || 0;
    if (limiteNovo != null && carrosAtuais > limiteNovo) {
      const ok = window.confirm(
        `Atenção: ${assinante.nome} tem ${carrosAtuais} carros, mas o plano ${PLANO_LABELS[novoPlano]} permite apenas ${limiteNovo}. ` +
        `Os carros excedentes continuarão visíveis, mas o cliente não poderá criar novos. Confirmar mudança?`
      );
      if (!ok) return;
    }
    try {
      await axios.put(`${API}/super-admin/assinantes/${assinante.id}/plano`, { plano: novoPlano });
      toast.success(`Plano de ${assinante.nome} atualizado para ${PLANO_LABELS[novoPlano]}`);
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao alterar plano");
    }
  };

  const filtrados = assinantes.filter(a => {
    if (!busca.trim()) return true;
    const q = busca.toLowerCase();
    return [a.nome, a.email, a.nome_loja, a.telefone].some(v => (v || "").toLowerCase().includes(q));
  });

  if (!user.super_admin) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center">
        <Shield className="h-16 w-16 mx-auto text-red-500 mb-4" />
        <h2 className="text-2xl font-bold mb-2">Acesso restrito</h2>
        <p className="text-gray-600">Esta área é exclusiva do Super Admin.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestao-assinantes-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: "Work Sans" }}>
            <Crown className="h-7 w-7 text-amber-500" /> Gestão de Assinantes
          </h2>
          <p className="text-sm text-gray-500">Cadastre, bloqueie e gerencie cada empresa assinante do sistema.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="btn-novo-assinante">
          <Plus className="h-4 w-4" /> Novo Assinante
        </Button>
      </div>

      {/* Métricas globais */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-emerald-200">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase font-bold">Assinantes Ativos</p>
            <p className="text-2xl font-extrabold text-emerald-700" data-testid="metric-ativos">
              {assinantes.filter(a => !a.bloqueado).length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase font-bold">Bloqueados</p>
            <p className="text-2xl font-extrabold text-red-600" data-testid="metric-bloqueados">
              {assinantes.filter(a => a.bloqueado).length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-blue-200">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase font-bold">Total Clientes</p>
            <p className="text-2xl font-extrabold text-blue-700">
              {assinantes.reduce((s, a) => s + (a.stats?.clientes || 0), 0)}
            </p>
          </CardContent>
        </Card>
        <Card className="border-purple-200">
          <CardContent className="p-4">
            <p className="text-xs text-gray-500 uppercase font-bold">Total Notas</p>
            <p className="text-2xl font-extrabold text-purple-700">
              {assinantes.reduce((s, a) => s + (a.stats?.promissorias || 0), 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Busca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Buscar por nome, email, loja ou telefone..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="pl-10"
          data-testid="input-busca-assinante"
        />
      </div>

      {/* Lista de assinantes */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">Carregando...</div>
      ) : filtrados.length === 0 ? (
        <Card className="bg-gray-50">
          <CardContent className="p-8 text-center text-gray-500">Nenhum assinante encontrado.</CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtrados.map(a => (
            <Card
              key={a.id}
              className={`relative overflow-hidden ${a.bloqueado ? "border-red-300 bg-red-50/40" : a.super_admin ? "border-amber-400 bg-amber-50/40" : "border-emerald-200"}`}
              data-testid={`assinante-card-${a.id}`}
            >
              {a.super_admin && (
                <div className="absolute top-0 right-0 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg flex items-center gap-1">
                  <Crown className="h-3 w-3" /> SUPER ADMIN
                </div>
              )}
              {a.bloqueado && (
                <div className="absolute top-0 right-0 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-lg flex items-center gap-1">
                  <Lock className="h-3 w-3" /> BLOQUEADO
                </div>
              )}
              <CardHeader className="pb-2 pt-5">
                <CardTitle className="text-base">{a.nome}</CardTitle>
                {a.nome_loja && <p className="text-xs text-gray-600">{a.nome_loja}</p>}
              </CardHeader>
              <CardContent className="space-y-2 text-xs">
                <p className="text-gray-600"><strong>Email:</strong> {a.email}</p>
                {a.telefone && <p className="text-gray-600"><strong>Tel:</strong> {a.telefone}</p>}
                <p className="text-gray-500"><strong>Desde:</strong> {formatDate(a.criado_em)}</p>
                {/* Seletor de plano (super admin gerencia manualmente) */}
                {!a.super_admin && (
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-gray-600 font-semibold">Plano:</span>
                    <select
                      value={a.plano || "start"}
                      onChange={(e) => alterarPlano(a, e.target.value)}
                      className="text-xs border rounded px-2 py-1 bg-white font-bold"
                      data-testid={`select-plano-${a.id}`}
                    >
                      <option value="start">Start (R$ 197)</option>
                      <option value="pro">Pro (R$ 247)</option>
                      <option value="premium">Premium (R$ 697)</option>
                      <option value="corporativo">Corporativo</option>
                    </select>
                  </div>
                )}
                {a.super_admin && (
                  <p className="text-xs"><strong>Plano:</strong> <span className="text-amber-700 font-bold">Premium (Super Admin)</span></p>
                )}
                {a.bloqueado && a.bloqueado_motivo && (
                  <p className="text-red-700 bg-red-100 rounded px-2 py-1 text-[11px]">Motivo: {a.bloqueado_motivo}</p>
                )}
                <div className="grid grid-cols-3 gap-1 pt-2 border-t">
                  <div className="text-center">
                    <Users className="h-3.5 w-3.5 mx-auto text-blue-600" />
                    <p className="text-[10px] text-gray-500">Clientes</p>
                    <p className="font-bold text-blue-700">{a.stats?.clientes || 0}</p>
                  </div>
                  <div className="text-center">
                    <FileText className="h-3.5 w-3.5 mx-auto text-purple-600" />
                    <p className="text-[10px] text-gray-500">Notas</p>
                    <p className="font-bold text-purple-700">{a.stats?.promissorias || 0}</p>
                  </div>
                  <div className="text-center">
                    <Truck className="h-3.5 w-3.5 mx-auto text-emerald-600" />
                    <p className="text-[10px] text-gray-500">Carros</p>
                    <p className="font-bold text-emerald-700">{a.stats?.carros || 0}</p>
                  </div>
                </div>
                {!a.super_admin && (
                  <div className="mt-2 space-y-1.5">
                    <Button
                      size="sm"
                      onClick={() => abrirBlock(a)}
                      className={`w-full gap-1.5 ${a.bloqueado ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} text-white`}
                      data-testid={`btn-${a.bloqueado ? "desbloquear" : "bloquear"}-${a.id}`}
                    >
                      {a.bloqueado ? <><Unlock className="h-3.5 w-3.5" /> Desbloquear</> : <><Lock className="h-3.5 w-3.5" /> Bloquear</>}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => abrirExcluir(a)}
                      className="w-full gap-1.5 text-red-700 border-red-300 hover:bg-red-50"
                      data-testid={`btn-excluir-${a.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Excluir
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog: criar assinante */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-novo-assinante">
          <DialogHeader>
            <DialogTitle>Novo Assinante</DialogTitle>
          </DialogHeader>
          <form onSubmit={criar} className="space-y-3">
            <div>
              <Label>Nome do responsável *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required data-testid="input-novo-nome" />
            </div>
            <div>
              <Label>Email (login) *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="input-novo-email" />
            </div>
            <div>
              <Label>Senha *</Label>
              <PasswordInput value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} required minLength={4} data-testid="input-novo-senha" />
            </div>
            <div>
              <Label>Nome da loja/empresa</Label>
              <Input value={form.nome_loja} onChange={(e) => setForm({ ...form, nome_loja: e.target.value })} data-testid="input-novo-loja" />
            </div>
            <div>
              <Label>Telefone (opcional)</Label>
              <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="input-novo-telefone" />
            </div>
            <div>
              <Label>Plano inicial</Label>
              <select
                value={form.plano}
                onChange={(e) => setForm({ ...form, plano: e.target.value })}
                className="w-full border rounded px-3 py-2 mt-1 text-sm"
                data-testid="select-novo-plano"
              >
                <option value="start">Start (R$ 197/mês)</option>
                <option value="pro">Pro (R$ 247/mês)</option>
                <option value="premium">Premium (R$ 697/mês)</option>
                <option value="corporativo">Corporativo</option>
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={creating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-criar-assinante">
                {creating ? "Criando..." : "Criar Assinante"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: bloquear/desbloquear */}
      <Dialog open={blockOpen} onOpenChange={setBlockOpen}>
        <DialogContent data-testid="dialog-bloquear-assinante">
          <DialogHeader>
            <DialogTitle>
              {blockTarget?.bloqueado ? "Desbloquear" : "Bloquear"} — {blockTarget?.nome}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              {blockTarget?.bloqueado
                ? "Ao desbloquear, este assinante voltará a conseguir acessar o sistema normalmente (incluindo todos os vendedores e secretários dele)."
                : "Ao bloquear, este assinante e TODOS os usuários da empresa dele (vendedores, secretários) ficarão impedidos de fazer login até serem desbloqueados."}
            </p>
            {!blockTarget?.bloqueado && (
              <div>
                <Label>Motivo (opcional, visível ao tentar logar)</Label>
                <Input value={blockMotivo} onChange={(e) => setBlockMotivo(e.target.value)} placeholder="Ex: Pagamento atrasado" data-testid="input-motivo-bloqueio" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBlockOpen(false)}>Cancelar</Button>
            <Button
              onClick={confirmarBlock}
              disabled={blockSaving}
              className={blockTarget?.bloqueado ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
              data-testid="btn-confirmar-bloqueio"
            >
              {blockSaving ? "Salvando..." : (blockTarget?.bloqueado ? "Confirmar Desbloqueio" : "Confirmar Bloqueio")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: EXCLUIR assinante (irreversível) */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="max-w-md" data-testid="dialog-excluir-assinante">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Excluir Assinante
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-xs text-red-900">
              <p className="font-bold mb-1">⚠️ Esta ação é IRREVERSÍVEL.</p>
              <p>Ao confirmar, serão apagados PERMANENTEMENTE:</p>
              <ul className="list-disc pl-4 mt-1 space-y-0.5">
                <li>O cadastro do assinante <strong>{delTarget?.nome}</strong> ({delTarget?.email})</li>
                <li>Todos os vendedores e secretários dele</li>
                <li>Clientes, promissórias, carros, rotas, produtos, estoque, pedidos, caixa e movimentações</li>
                <li>Configurações da loja, logos e templates de mensagem</li>
              </ul>
            </div>
            <div>
              <Label className="text-xs">Para confirmar, digite <strong>EXCLUIR</strong> abaixo:</Label>
              <Input
                value={delConfirm}
                onChange={(e) => setDelConfirm(e.target.value)}
                placeholder='Digite "EXCLUIR"'
                data-testid="input-confirm-excluir"
              />
            </div>
            <div>
              <Label className="text-xs">Sua senha de Super Admin:</Label>
              <PasswordInput
                value={delSenha}
                onChange={(e) => setDelSenha(e.target.value)}
                placeholder="Senha do super admin"
                data-testid="input-senha-excluir"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)}>Cancelar</Button>
            <Button
              onClick={confirmarExcluir}
              disabled={delLoading || delConfirm.trim().toUpperCase() !== "EXCLUIR" || !delSenha}
              className="bg-red-600 hover:bg-red-700 text-white gap-1.5"
              data-testid="btn-confirmar-excluir"
            >
              <Trash2 className="h-4 w-4" /> {delLoading ? "Excluindo..." : "Excluir Permanentemente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
